import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "mantine-react-table/styles.css";
import "./theme/tokens.css";
import "./theme/layout.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { createTheme, MantineProvider, type MantineColorsTuple } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { ModalsProvider } from "@mantine/modals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MsalProvider } from "@azure/msal-react";
import { BrowserRouter } from "react-router";

import App from "./App";
import { loadRuntimeConfig } from "./runtime-config";
import { buildMsalInstance } from "./auth/msal";

// Indigo-ish ramp matching the prototype's accent (oklch(0.58 0.15 265)).
// Mantine wants 10 hex shades; these are an approximate sRGB rendering of
// the OKLCH curve with hue ≈ 265.
const accent: MantineColorsTuple = [
  "#eef0fb",
  "#d9deef",
  "#b3bbe1",
  "#8b97d3",
  "#6c79c5",
  "#5666bd",
  "#4a5cba",
  "#3b4ea4",
  "#324593",
  "#253a82",
];

const theme = createTheme({
  primaryColor: "accent",
  primaryShade: { light: 6, dark: 4 },
  fontFamily: "var(--font-ui)",
  fontFamilyMonospace: "var(--font-mono)",
  defaultRadius: "sm",
  colors: { accent },
  cursorType: "pointer",
});

async function bootstrap() {
  const cfg = await loadRuntimeConfig();
  const msal = buildMsalInstance(cfg);
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (accounts[0]) msal.setActiveAccount(accounts[0]);

  // Track Mantine's color scheme on the <html> element so our custom CSS
  // tokens flip in lockstep.
  const syncTheme = (scheme: "light" | "dark") => {
    document.documentElement.setAttribute("data-theme", scheme);
  };
  syncTheme(
    (localStorage.getItem("mantine-color-scheme-value") as "light" | "dark" | null) ??
      "light",
  );

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false },
    },
  });

  const root = createRoot(document.getElementById("root")!);
  root.render(
    <React.StrictMode>
      <MantineProvider
        theme={theme}
        defaultColorScheme="light"
      >
        <Notifications position="bottom-right" />
        <ModalsProvider>
          <MsalProvider instance={msal}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <App apiScope={cfg.apiScope} />
              </BrowserRouter>
            </QueryClientProvider>
          </MsalProvider>
        </ModalsProvider>
      </MantineProvider>
    </React.StrictMode>,
  );
}

bootstrap().catch((err) => {
  // eslint-disable-next-line no-console
  console.error("Bootstrap failed:", err);
  document.body.innerHTML = `<pre style="padding:2rem;font-family:monospace">${
    String(err?.message ?? err)
  }</pre>`;
});
